import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StaticPageLayout } from '../layouts/static-page-layout';
import { useAuth } from '@/components/auth/AuthProvider';
import { useEffect } from 'react';
import { 
  Brain, 
  Target, 
  Calendar,
  Clock,
  ArrowRight,
  CheckCircle,
  BookOpen,
  Users,
  TrendingUp,
  Eye,
  EyeOff,
  Zap,
  AlertCircle,
  BarChart3,
  Settings,
  Lightbulb
} from 'lucide-react';

export function ComprehensionAssessmentFeaturePage() {
  const { user } = useAuth();
  
  // Set page title
  useEffect(() => {
    document.title = 'Comprehension Assessment - AceHorizon';
  }, []);

  const assessmentFeatures = [
    {
      icon: Calendar,
      title: "Quarterly Assessment Schedule",
      description: "Four comprehensive assessments throughout the academic year, strategically spaced every three months to track your evolving understanding.",
      color: "bg-blue-500"
    },
    {
      icon: Target,
      title: "20 Carefully Crafted Questions",
      description: "Each assessment contains exactly 20 questions designed to evaluate different aspects of your comprehension across all subjects.",
      color: "bg-green-500"
    },
    {
      icon: Brain,
      title: "Deep Understanding Analysis",
      description: "Our AI analyzes not just your answers, but your reasoning patterns, concept connections, and thinking methodology.",
      color: "bg-purple-500"
    },
    {
      icon: Settings,
      title: "Intelligent Content Tailoring",
      description: "AceHorizon uses your assessment results to customize every concept explanation, example, and learning material to match your comprehension level perfectly.",
      color: "bg-orange-500"
    },
    {
      icon: EyeOff,
      title: "Stress-Free Assessment",
      description: "Results are never shown to you, eliminating test anxiety while still providing valuable insights to the system.",
      color: "bg-indigo-500"
    }
  ];

  const assessmentWorkflow = [
    {
      step: "1",
      icon: Target,
      title: "Take Assessment",
      description: "Complete 20 thoughtfully designed questions every three months",
      color: "bg-blue-500"
    },
    {
      step: "2", 
      icon: Brain,
      title: "AI Analysis",
      description: "Our system evaluates your comprehension patterns and learning style",
      color: "bg-green-500"
    },
    {
      step: "3",
      icon: Settings,
      title: "Profile Creation",
      description: "A personalized learning profile is created based on your responses",
      color: "bg-purple-500"
    },
    {
      step: "4",
      icon: Zap,
      title: "Adaptive Learning",
      description: "All future explanations are tailored to your specific comprehension level",
      color: "bg-orange-500"
    }
  ];

  const benefits = [
    {
      icon: Brain,
      title: "Personalized Learning Experience",
      description: "Every explanation and concept breakdown is tailored to match your specific comprehension level and learning style, making complex topics more accessible."
    },
    {
      icon: Target,
      title: "Precision Learning",
      description: "No more one-size-fits-all explanations. Our system tailors every concept explanation to your exact comprehension level, ensuring you receive content that's neither too basic nor too advanced for your understanding."
    },
    {
      icon: Clock,
      title: "Efficient Study Time",
      description: "Spend time learning, not struggling with explanations that are too basic or too advanced. Every minute counts towards meaningful progress."
    },
    {
      icon: TrendingUp,
      title: "Continuous Improvement",
      description: "Regular assessments ensure your learning experience evolves with your growing knowledge, maintaining optimal challenge levels."
    },
    {
      icon: Eye,
      title: "Stress-Free Progress",
      description: "Since results aren't displayed, you can focus purely on learning without performance anxiety or comparison with others."
    },
    {
      icon: Lightbulb,
      title: "Deep Understanding Focus",
      description: "Rather than memorization, our system emphasizes true comprehension, helping you build lasting knowledge foundations."
    }
  ];

  return (
    <StaticPageLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Hero Section */}
        <div className="text-center py-12 sm:py-20">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-400 dark:via-indigo-400 dark:to-purple-400 bg-clip-text text-transparent leading-tight">
              Comprehension Assessment System
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
              Our revolutionary assessment system works <em className="text-primary font-semibold">behind the scenes</em> to understand 
              your learning patterns and <em className="text-primary font-semibold">personalize every explanation</em> you receive.
            </p>
            <div className="flex items-center justify-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4" />
                <span>4 times per year</span>
              </div>
              <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
              <div className="flex items-center space-x-2">
                <Target className="h-4 w-4" />
                <span>20 questions each</span>
              </div>
              <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
              <div className="flex items-center space-x-2">
                <EyeOff className="h-4 w-4" />
                <span>Results stay private</span>
              </div>
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 dark:from-emerald-400 dark:via-teal-400 dark:to-cyan-400 bg-clip-text text-transparent mb-6">
              How Our Assessment Works
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              A unique approach that focuses on understanding rather than evaluation
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            {assessmentFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="border-0 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardHeader className="text-center pb-4">
                    <div className={`w-16 h-16 ${feature.color} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-center text-sm leading-relaxed">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Assessment Workflow */}
        <div className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 dark:from-violet-400 dark:via-purple-400 dark:to-fuchsia-400 bg-clip-text text-transparent mb-6">
              The Assessment Journey
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              From assessment to personalized learning - here's how the process works
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {assessmentWorkflow.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div key={index} className="text-center relative">
                  {/* Connector line */}
                  {index < assessmentWorkflow.length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-1/2 w-full h-0.5 bg-gradient-to-r from-blue-300 to-purple-300 dark:from-blue-600 dark:to-purple-600 transform translate-x-8"></div>
                  )}
                  
                  <div className="relative z-10">
                    <div className={`w-16 h-16 ${item.color} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg relative`}>
                      <IconComponent className="h-8 w-8 text-white" />
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center text-xs font-bold text-gray-700 dark:text-gray-300">
                        {item.step}
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 via-blue-600 to-teal-600 dark:from-indigo-400 dark:via-blue-400 dark:to-teal-400 bg-clip-text text-transparent mb-6">
              Why This Approach Works
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              The benefits of our comprehension-focused assessment system
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-all duration-300 border-l-4 border-l-blue-500">
                  <CardHeader>
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                        <IconComponent className="h-5 w-5 text-white" />
                      </div>
                      <CardTitle className="text-lg">{benefit.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Privacy & Transparency */}
        <div className="py-16 bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900/20 rounded-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Assessment Privacy & Ethics
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Your assessment data is handled with complete transparency and privacy
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {[
              {
                icon: EyeOff,
                title: "Results Stay Private",
                description: "Assessment results are never displayed to students, eliminating performance anxiety and test stress."
              },
              {
                icon: Settings,
                title: "Internal Use Only", 
                description: "Results are used exclusively by our AI system to improve your learning experience."
              },
              {
                icon: BarChart3,
                title: "No Comparison or Ranking",
                description: "Your data is never used for comparison with other students or academic ranking purposes."
              }
            ].map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-gray-700 dark:bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Designed for JK BOSE Secondary Students
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Our comprehension assessment is specifically aligned with the JK BOSE board curriculum for 
                  9th and 10th standard students, ensuring relevance and effectiveness for your academic journey.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 rounded-2xl p-8 text-center my-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 via-blue-600 to-teal-600 dark:from-indigo-400 dark:via-blue-400 dark:to-teal-400 bg-clip-text text-transparent mb-6">
              Experience Personalized Learning
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
              Join AceHorizon and experience how our comprehension assessment system creates a truly personalized learning journey for you.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Button asChild size="lg" className="px-12 py-4 text-lg">
                <Link to={user ? "/dashboard" : "/login"}>
                  {user ? "Go to Dashboard" : "Start Your Journey"}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>{user ? "Access your personalized learning experience" : "First assessment guides your entire experience"}</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </StaticPageLayout>
  );
}

export default ComprehensionAssessmentFeaturePage;
